import { connect } from "react-redux";
import React from "react";
import Helmet from "react-helmet";
import Waypoint from "react-waypoint";
import Scroll from "react-scroll";
import assign from "object-assign";
import PropTypes from "prop-types";
import { Style } from "radium";
import ModalWrapper from "backpack-ui/dist/components/modal/wrapper";
import { MultiStepWrapper } from "backpack-ui/dist/components/multiStep";
import Toast from "@lonelyplanet/dotcom-core/dist/components/toast";
import imgix from "@lonelyplanet/dotcom-core/dist/helpers/imgix";
import FeatureDiscover from "./featureDiscover";
import DiscoveryFlyout from "./featureDiscover/discoveryFlyout";
import ActivityPoiComponent from "./activity";
import CollectionComponent from "./collection";
import NarrativeComponent from "./narrative";
import SightPoiComponent from "./sight";
import TransportPoiComponent from "./transport";
import { connected as RestaurantPoiComponent } from "./restaurant";
import { connected as HotelPoiComponent } from "./hotel";
import actions from "../actions";
import { SchemaEvent, SchemaReview } from "./schema";
import styles from "./styles";
import hotelProvider from "../../shared/hotelProvider";
import { pageView } from "../../shared/actions/analytics";
import DataLayer from "../../shared/components/dataLayer";
import buildPlaceDimensions from "../../shared/udo/buildPlaceDimensions";
import createMetaData from "../../../../server/lib/helpers/metaData";
import { getAttribution } from "../selectors";
import AddBookmarkModal from "./addBookmarkModal";

import {
  RESOURCE_MAP,
  RESOURCE_KIND_MAP,
} from "../../../../server/routes/constants";
import { POI_RENDER } from "../constants";

import resourceTypes from "../../../../server/lib/open-planet/resourceTypes";
import { poiTypes } from "../../../../server/lib/open-planet/poiTypes";

// import Ad from "react-google-publisher-tag";

/*
 <div style={adWrapperStyle}>
   <Ad path="/9885583/LonelyPlanet.com/Articles" />
 </div>
 */

const toastDuration = 5000;

/**
 * Renders a list of pois
 */
export default class PoisComponent extends React.PureComponent {
  static _hotelImages({ provider, images }) {
    if (Array.isArray(images)) {
      if (provider.type === "hostelworld") {
        const hwImages = images.map(i => i.links.pixels);

        // TODO: Check to see if we can remove thumbnails on the back-end
        if (hwImages && hwImages.length) {
          for (let i = hwImages.length - 1; i >= 0; i -= 1) {
            if (
              hwImages[i].includes("_s.jpg") ||
              hwImages[i].includes("_s.JPG") ||
              hwImages[i].includes("_l.jpg") ||
              hwImages[i].includes("_l.JPG")
            ) {
              hwImages.splice(i, 1);
            }
          }
        }

        return hwImages && hwImages.length
          ? hwImages.map(i => ({ src: i, provider: provider.type }))
          : [];
      }
      return images.map(i => ({
        src: i.links.pixels,
        provider: provider.type,
      }));
    }

    return images && images.links && images.links.pixels
      ? [{ src: images.links.pixels, provider: provider.type }]
      : [];
  }

  static _partnerActivityImages({ poi }) {
    const provider = poi.provider;

    if (Array.isArray(poi.media.images) && poi.media.images.length) {
      const images = poi.media.images;

      if (poi.links.image && images.indexOf(poi.links.image) === -1) {
        images.unshift(poi.links.image);
      }

      return images.map(i => ({ src: i, provider }));
    }

    const url = poi.links.image;
    return [{ src: url, provider }];
  }

  static _poiImages({ poi, mediaParams }) {
    const provider = "media";

    if (!poi.imageAssociations) return [];

    if (Array.isArray(poi.imageAssociations)) {
      return poi.imageAssociations.reduce((memo, i) => {
        if (i.from) {
          memo.push({
            src: imgix(i.from.links.pixels, mediaParams, provider),
            provider,
          });
        }
        return memo;
      }, []);
    }

    const url = poi.imageAssociations.from
      ? poi.imageAssociations.from.links.pixels
      : "";
    return url ? [imgix(url, mediaParams, provider)] : [];
  }

  static getImages(poi) {
    const params = {
      auto: "enhance",
      w: 770,
      h: 430,
      fit: "max",
    };
    const mediaParams = assign({}, params, { fit: "crop" });

    if (poi.type === resourceTypes.partner_activity) {
      return PoisComponent._partnerActivityImages({ poi, params });
    }

    if (poi.providers && poi.type === resourceTypes.lodging) {
      const provider = hotelProvider(poi.providers);
      return PoisComponent._hotelImages({
        provider,
        params,
        images: poi.images,
      });
    }

    if (poi.type === resourceTypes.poi || poi.type === resourceTypes.collections) {
      return PoisComponent._poiImages({ poi, mediaParams });
    }

    if (poi.links && poi.links.image) {
      return [imgix(poi.links.image, params, poi.provider)];
    }

    return [];
  }

  constructor() {
    super();

    this._handleWaypointEnter = this._handleWaypointEnter.bind(this);
    this._enteredPoi = this._enteredPoi.bind(this);
    this._scrollToNextPoi = this._scrollToNextPoi.bind(this);
    this.createBookmark = this.createBookmark.bind(this);
    this.showBookmark = this.showBookmark.bind(this);
    this.showToast = this.showToast.bind(this);

    this.ads = {};

    this.state = {
      totalPois: 1, // How many POIs have rendered
      showFeatureDiscover: false,
      showToast: false,
      linkContent: "",
      toastMessage: "",
      toastUrl: "",
    };
  }

  componentDidMount() {
    // Make sure to reset the scroll to the top of the page
    window.onunload = () => {
      document.body.scrollTop = 0;
    };

    const { dispatch, params, poi } = this.props;

    if (this._shouldFetchPoi(params.id)) {
      dispatch(actions.fetchPoi({ id: params.id, placeId: params.placeId }));
    }
    if (
      poi &&
      poi.type !== resourceTypes.collections &&
      poi.type !== resourceTypes.narratives &&
      !poi.partnerActivities
    ) {
      dispatch(
        actions.fetchPartnerActivities({
          id: params.id,
          placeId: params.placeId,
        })
      );
    }

    dispatch(actions.initPoi(poi));
  }

  componentWillReceiveProps(nextProps) {
    const { poi, dispatch, params } = nextProps;

    if (
      !this.props.poi &&
      poi &&
      poi.type !== resourceTypes.collections &&
      poi.type !== resourceTypes.narratives &&
      !poi.partnerActivities
    ) {
      dispatch(actions.fetchPartnerActivities({
        id: params.id,
        placeId: params.placeId,
      }));
    }

    if (this.props.poi && this._shouldFetchPoi(params.id)) {
      dispatch(
        actions.fetchPoi({
          id: params.id,
          placeId: params.placeId,
        })
      );
    }
  }

  componentDidUpdate(prevProps) {
    const { poi: newPoi } = this.props;
    const { poi: oldPoi } = prevProps;

    let hasNewPoiLoaded = false;
    if (!oldPoi && newPoi) {
      hasNewPoiLoaded = true;
    } else if (oldPoi && newPoi && oldPoi.id !== newPoi.id) {
      hasNewPoiLoaded = true;
    }

    if (hasNewPoiLoaded) {
      window.scrollTo(0, 0); // Scroll up to top when POI is reloaded.
      this.props.dispatch(pageView(POI_RENDER));
    }
  }

  componentWillUnmount() {
    this.props.dispatch(actions.poiUnmount());
  }

  _scrollToNextPoi(nextPoi) {
    const scroller = Scroll.scroller;
    const nextId = `poi-${nextPoi.id}`;
    const adHeight = this.ads[`ad-${this.props.poiId}`].offsetHeight;

    let scrollOffset = 0;

    if (adHeight > 200) {
      scrollOffset = adHeight / 2;
    }

    scroller.scrollTo(nextId, {
      delay: 0,
      duration: 500,
      offset: scrollOffset,
      smooth: true,
    });
  }

  _shouldFetchPoi(nextPoi) {
    return !this.props.poi || nextPoi !== this.props.poi.id;
  }

  _handleWaypointEnter({ event }) {
    // const {
    //   dispatch,
    //   pois,
    //   nextPoisLink,
    // } = this.props;

    // The first time the component loads the waypoint triggers with an empty event
    // This makes sure to not accidentally re-fetch data on first mount coming from lists
    if (!event || !this) return;

    // Once we've gotten through 10 POIs, load the next 10
    // turn off fetch next until we fix infinite scroll
    // if (!this.props.loading && this.state.totalPois >= pois.length) {
    //   dispatch(actions.fetchNext(
    //     this.props.place.id,
    //     this.props.params.poiType,
    //     this.props.params.resource,
    //     nextPoisLink
    //   ));
    // }

    // We reached the bottom of an POI, so we're about to render another one
    // this.setState({
    //   totalPois: this.state.totalPois + 1,
    // });
  }

  _enteredPoi({ event }) {
    if (!event || !this) return;

    // remove url replacement until we fix place issues
    // window.history.replaceState(null, `${id} - Lonely Planet`, createDetailUrlFromResource(
    //   activePoi,
    //   links,
    //   place.id,
    // ));
  }

  showToast(message, callback, url) {
    this.setState({
      showToast: true,
      toastMessage: message,
      toastUrl: url,
      linkContent: url ? "See list" : "",
    }, () => {
      this.setState({
        showToast: false,
      }, callback);
    });
  }

  createBookmark({ list, bookmark }) {
    const { dispatch, poi, user } = this.props;

    if (!user.id) {
      window.location.hash = "#login";
      return;
    }

    const checked = bookmark ? !bookmark.checked : true;

    dispatch(actions.createBookmark({
      list,
      userId: user.id,
      bookmark: {
        id: bookmark && bookmark.id,
        note: "",
        checked,
        target: {
          id: poi.id,
          type: this.props.resource,
        },
      },
    }));

    const message = checked ? `Added to ${list.name}.` : `Removed from ${list.name}.`;
    const toastUrl = checked ? this.createUserListsLink(list) : "";

    const timeout = () => {
      setTimeout(() => {
        this.setState({
          showFeatureDiscover: true,
        });
      }, 2000);
    };

    this.showToast(message, timeout, toastUrl);
  }

  createUserListsLink({ id }) {
    const { user } = this.props;
    const url = window.lp.hosts.profile;

    return `${url}/profile/${user.username}/lists/${id}`;
  }

  showBookmark() {
    const { poi, bookmarkLists, user } = this.props;

    if (!user) {
      return null;
    }

    const lists = bookmarkLists.lists;
    const hasLists = Object.keys(lists).length > 0;

    const listIds = hasLists && Object.keys(lists)
      .filter(listId => lists[listId].entries.find(e => e.checked && e.target.id === poi.id));

    return listIds.length > 0;
  }

  render() {
    const { poi, links, place, loading, mobile, resource, kind, pois } = this.props;

    const types = {
      sleeping: HotelPoiComponent,
      drinking_nightlife: SightPoiComponent,
      transport: TransportPoiComponent,
      partner_activity: ActivityPoiComponent,
      activities: SightPoiComponent,
      entertainment: SightPoiComponent,
      shopping: SightPoiComponent,
      restaurants: RestaurantPoiComponent,
      eating: RestaurantPoiComponent,
      sights: SightPoiComponent,
      info: SightPoiComponent,
      festivals_events: SightPoiComponent,
      collections: CollectionComponent,
      narratives: NarrativeComponent,
    };

    const meta = createMetaData(
      { kind, resource, ...poi },
      place || {},
      "detail"
    );

    const isTopChoice = poi && poi.poiAttributes && poi.poiAttributes.filter(item => item.name === "top_choice").length > 0;

    const attributes = poi || {};
    let Component = types.sights;

    if (attributes.poiType) {
      Component = types[attributes.poiType.toLowerCase()];
    } else if (types[attributes.type]) {
      Component = types[attributes.type];
    }

    const images = poi ? PoisComponent.getImages(poi) : [];
    attributes.slug = `pois/${attributes.id}`;

    const metaImage = images[0] && (images[0].src || images[0]);

    const lists = this.props.bookmarkLists.lists;

    if (!poi || !poi.id) {
      return (<div className={`PoiComponent ${loading ? "loading" : ""}`} />);
    }


    return (
      <div className="PoiComponent">
        <Toast
          animationDuration={200}
          buttonLabel={this.state.linkContent}
          direction="bottom"
          duration={toastDuration}
          message={this.state.toastMessage}
          type="success"
          url={this.state.toastUrl}
          visible={this.state.showToast}
          affixed
        />

        {this.state.showFeatureDiscover &&
          <FeatureDiscover
            featureName="firstListCreated"
            removeOnOuterClick
            removeOnNavigate
          >
            <DiscoveryFlyout
              headline="View your lists"
              description="Access lists in global navigation."
              arrowPosition="right"
              style={styles.featureDiscovery.mainMenu}
            />
          </FeatureDiscover>
        }

        <Helmet
          title={meta.title}
          description={meta.description}
          link={(poi.type !== resourceTypes.narrative && poi.type !== resourceTypes.narratives) ? [
            {
              rel: "canonical",
              href: meta.canonical,
            },
          ] : []}
          meta={[
            { name: "title", content: meta.title },
            { name: "description", content: meta.description },
            { property: "og:title", content: meta.title },
            { property: "og:description", content: meta.description },
            { property: "og:image", content: metaImage },
            { name: "title", class: "swiftype", "data-type": "string", content: poi.name },
            { name: "op-type", class: "swiftype", "data-type": "string", content: poi.type },
            { name: "op-id", class: "swiftype", "data-type": "string", content: poi.id },
            { name: "body", class: "swiftype", "data-type": "text", content: meta.description },
            { name: "op-parent-id", class: "swiftype", "data-type": "string", content: place.id },
            { name: "op-parent-name", class: "swiftype", "data-type": "string", content: place.name },
            { name: "thumbnail", class: "swiftype", "data-type": "string", content: metaImage },
            { name: "grade", class: "swiftype", "data-type": "float", content: poi.score || "0" },
            { name: "top-choice", class: "swiftype", "data-type": "integer", content: isTopChoice ? "1" : "0" },
          ]}
        />

        <DataLayer
          data={{
            poiName: poi && poi.name,
            content_group_2: "pois",
            kind,
            resource,
            ...buildPlaceDimensions(place),
          }}
        />

        <div>
          <Style rules={styles.bookmarkListStyle} />

          <Waypoint
            onEnter={event => {
              this._enteredPoi(event, poi.id);
            }}
            onLeave={this._leftPoi}
            fireOnRapidScroll
          />

          {(poi.type === resourceTypes.poi &&
            poi.poiType.toLowerCase() === poiTypes.festivals_events.name) &&
            <SchemaEvent poi={poi} />
          }

          {((poi.type === resourceTypes.poi || poi.type === resourceTypes.lodging) &&
            poi.poiType.toLowerCase() !== poiTypes.festivals_events.name) &&
            <SchemaReview poi={poi} />
          }

          <ModalWrapper>
            {(isOpen, toggle) => (
              <div>
                <Component
                  poi={attributes}
                  place={place}
                  breadcrumb={links}
                  images={images}
                  attribution={this.props.attribution}
                  params={this.props.params}
                  showToast={this.showToast}
                  onClickBookmark={() => {
                    if (!this.props.user.id) {
                      window.location.hash = "#login";
                    } else {
                      toggle();
                    }
                  }}
                  marked={this.showBookmark()}
                />

                <MultiStepWrapper totalSteps={2}>
                  {(currentStep, goToNextStep, goToPreviousStep, setCurrentStep) => (
                    <AddBookmarkModal
                      currentStep={currentStep}
                      goToNextStep={goToNextStep}
                      goToPreviousStep={goToPreviousStep}
                      setCurrentStep={setCurrentStep}
                      isOpen={isOpen}
                      toggle={toggle}
                      clearListData={this.clearListData}
                      onSaveList={this.createBookmark}
                      onCreateBookmark={this.createBookmark}
                      lists={lists}
                      poi={poi}
                      pois={pois}
                      mobile={mobile.isMobile}
                    />
                  )}
                </MultiStepWrapper>
              </div>
            )}
          </ModalWrapper>
        </div>

        <Waypoint threshold={0.5} onEnter={this._handleWaypointEnter} />
      </div>
    );
  }
}

PoisComponent.contextTypes = {
  router: PropTypes.object.isRequired,
};

PoisComponent.propTypes = {
  dispatch: PropTypes.func,

  loading: PropTypes.bool,

  poi: PropTypes.object,

  links: PropTypes.array,

  place: PropTypes.object,

  /**
   * React router params
   */
  params: PropTypes.object,

  /**
   * Id of the first POI in the list
   */
  poiId: PropTypes.number,

  /**
   * object for determining to display mobile only items
   */
  mobile: PropTypes.object,
  resource: PropTypes.string,
  kind: PropTypes.string,
  attribution: PropTypes.arrayOf(PropTypes.object),
  user: PropTypes.object,
  bookmarkLists: PropTypes.object,
  /**
   * Cached POIs
   */
  pois: PropTypes.object,
};

const getResource = (state, props) => RESOURCE_MAP[props.params.resource];
const getKind = (state, props) => RESOURCE_KIND_MAP[props.params.kind];

const mapStateToProps = (state, ownProps) => ({
  poi: state.pois.initial,
  attribution: getAttribution(state),
  userAgent: state.userAgent,
  loading: state.loading,
  links: state.breadcrumb,
  poiId: parseInt(ownProps.params.id, 10),
  place: state.place,
  nextPoisLink: state.pois.next,
  mobile: state.mobile,
  kind: getKind(state, ownProps),
  resource: getResource(state, ownProps),
  bookmarkLists: state.bookmarkLists,
  user: state.user,
  pois: state.pois.cache,
});

const connected = connect(mapStateToProps)(PoisComponent);
export { connected };


// WEBPACK FOOTER //
// ./assets/pois/components/pois.jsx